import React, { useEffect, useState } from "react";
import { EntityLink } from "@/components/EntityLink";
import { api, euro } from "@/lib/api";
import { AdminBriefing } from "@/components/AdminBriefing";
import { InvoiceDataCheck } from "@/components/InvoiceDataCheck";
import { SimLowAlert } from "@/components/sim/SimLowAlert";
import { GpApprovedAlert } from "@/components/GpApprovedAlert";
import { DealerReturns } from "@/components/DealerReturns";
import { DealerMobileSummary } from "@/components/mobile/DealerMobileSummary";
import { AdminSimTodo } from "@/components/sim/AdminSimTodo";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Users, Smartphone, Zap, Wallet, Store, Clock, Package, Receipt, TrendingUp, CreditCard, ChevronRight, ArrowRightLeft } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Stat, StatStrip } from "@/components/StatTile";
import { useSimSummary, fmtRate } from "@/components/simstats/simStatsUtils";

const TIP = { background: "#111827", border: "1px solid #1F2937", borderRadius: 12, color: "#F9FAFB" };
const fmtBar = (v, n) => [euro(v), n === "revenue" ? "Fatturato" : "Provvigione"];
const legend = (v) => (v === "revenue" ? "Fatturato" : "Provvigione");
const ChartCard = ({ eyebrow, title, icon: Icon, testid, chartTestid, height, children }) => (
  <Card className="p-4 glow-border" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div><p className="text-[11px] font-medium tracking-wider uppercase text-muted-foreground">{eyebrow}</p><h2 className="font-head text-base font-semibold">{title}</h2></div>
      <Icon className="w-4 h-4 text-teal-500" />
    </div>
    <div style={{ width: "100%", height }} data-testid={chartTestid}><ResponsiveContainer>{children}</ResponsiveContainer></div>
  </Card>
);

const StatusBadge = ({ status }) => (status === "approved"
  ? <Badge className="bg-teal-600 hover:bg-teal-600 text-[11px] py-0.5" data-testid="dealer-status-badge">Account approvato</Badge>
  : status === "pending"
    ? <Badge className="bg-amber-500 hover:bg-amber-500 text-black text-[11px] py-0.5" data-testid="dealer-status-badge">In attesa di approvazione visura</Badge>
    : <Badge variant="destructive" className="text-[11px] py-0.5" data-testid="dealer-status-badge">Account sospeso</Badge>);

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [dealersCmp, setDealersCmp] = useState([]);
  const [adminWallet, setAdminWallet] = useState(null);
  const [usage, setUsage] = useState(null);
  const navigate = useNavigate();
  const sim = useSimSummary(user?.role === "admin" || user?.role === "dealer");
  const simTiles = sim ? [
    <Stat key="sa" icon={Smartphone} label="SIM attivate" value={sim.all.activated} hint={`questo mese ${sim.month.activated}`} testid="stat-sim-activated" onClick={() => navigate("/app/statistiche-sim?focus=activated")} />,
    <Stat key="sm" icon={ArrowRightLeft} label="SIM migrate out" value={sim.all.migrated} hint={`tasso ${fmtRate(sim.all.rate)}`} tone={sim.all.rate >= 10 ? "amber" : "primary"} testid="stat-sim-migrated" onClick={() => navigate("/app/statistiche-sim?focus=migrated")} />,
  ] : [];
  const isAdmin = stats?.role === "admin", isDealer = stats?.role === "dealer";

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/earnings").then((r) => setEarnings(r.data.months || [])).catch(() => {});
    if (user?.role === "admin") {
      api.get("/reports/commissions").then((r) => setDealersCmp(r.data.rows || [])).catch(() => {});
      api.get("/admin/wallet/movements").then((r) => setAdminWallet(r.data)).catch(() => {});
      api.get("/admin/dealer-usage/summary").then((r) => setUsage(r.data)).catch(() => {});
    }
  }, [user?.role]);

  const showEarnings = (isDealer || isAdmin) && earnings.length > 0;
  const showCmp = isAdmin && dealersCmp.length > 0;

  return (
    <div className="space-y-4" data-testid="overview-page">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h1 className="font-head text-2xl md:text-3xl font-bold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
        {user?.role === "dealer" && stats && <StatusBadge status={stats.status} />}
      </div>

      {user?.role === "dealer" && <div className="grid lg:grid-cols-2 gap-2.5 empty:hidden" data-testid="dashboard-alerts"><GpApprovedAlert /><InvoiceDataCheck /><SimLowAlert /></div>}
      {user?.role === "admin" && <AdminSimTodo />}
      {!stats && <p className="text-muted-foreground">Caricamento…</p>}

      {isAdmin && (
        <StatStrip testid="admin-stat-strip">
          <Stat icon={Store} label="Dealer / Agenzie" value={stats.total_dealers} testid="stat-dealers" onClick={() => navigate("/app/dealers")} />
          <Stat icon={Clock} label="In attesa di approvazione" value={stats.pending_dealers} tone={stats.pending_dealers ? "amber" : "primary"} testid="stat-pending" onClick={() => navigate("/app/dealers")} />
          {usage && <Stat icon={Users} label="Dealer inattivi 60+ gg" value={usage.inactive_60} hint={`credito fermo ${euro(usage.credit_idle_180)}`} tone={usage.inactive_60 ? "amber" : "primary"} testid="stat-inactive-dealers" onClick={() => navigate("/app/utilizzo-dealer")} />}
          <Stat icon={Package} label="Prodotti a catalogo" value={stats.total_products} testid="stat-products" />
          <Stat icon={Receipt} label="Ordini & ricariche" value={stats.total_orders} testid="stat-orders" />
          {simTiles}
          <Stat icon={Wallet} label="Fatturato totale" value={euro(stats.revenue)} testid="stat-revenue" />
          {adminWallet && <Stat icon={Wallet} label="Wallet admin" value={euro(adminWallet.balance)} hint={`−\u2060${euro(adminWallet.total_out)} / +\u2060${euro(adminWallet.total_in)}`} testid="admin-wallet-summary" onClick={() => navigate("/app/wallet-admin")} />}
        </StatStrip>
      )}

      {isDealer && (
        <StatStrip testid="dealer-stat-strip">
          {stats.pending_links > 0 && <Stat icon={CreditCard} tone="amber" label="Link da riscuotere" value={euro(stats.pending_links_amount || 0)} hint={`${stats.pending_links} link`} testid="stat-pending-links" onClick={() => navigate("/app/pagamenti")} />}
          <Stat icon={Users} label="Clienti" value={stats.customers} testid="stat-customers" />
          {simTiles}
          <Stat icon={Zap} label="Ricariche eseguite" value={stats.ricariche} testid="stat-ricariche" />
          <Stat icon={Receipt} label="Ordini totali" value={stats.orders} testid="stat-dealer-orders" onClick={() => navigate("/app/ordini")} />
          <Stat icon={Wallet} label="Fatturato" value={euro(stats.revenue)} testid="stat-dealer-revenue" />
          <Stat icon={TrendingUp} label="Provvigioni" value={euro(stats.commission)} hint="ripartite" testid="stat-commission" />
        </StatStrip>
      )}

      {isDealer && <DealerMobileSummary />}
      {isDealer && <DealerReturns />}

      {(showEarnings || showCmp) && (
        <div className={`grid gap-4 ${showEarnings && showCmp ? "xl:grid-cols-2" : ""}`}>
          {showEarnings && (
            <ChartCard eyebrow="Andamento" title="Guadagni e provvigioni · ultimi 6 mesi" icon={TrendingUp} testid="earnings-chart-card" chartTestid="earnings-chart" height={250}>
              <BarChart data={earnings} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
                <XAxis dataKey="label" stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} width={76} tickFormatter={(v) => euro(v, { maximumFractionDigits: 0 })} />
                <Tooltip contentStyle={TIP} formatter={fmtBar} cursor={{ fill: "rgba(37,99,235,0.08)" }} />
                <Legend formatter={legend} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#2563EB" radius={[6, 6, 0, 0]} maxBarSize={34} />
                <Bar dataKey="commission" fill="#0D9488" radius={[6, 6, 0, 0]} maxBarSize={34} />
              </BarChart>
            </ChartCard>
          )}
          {showCmp && (
            <ChartCard eyebrow="Confronto" title="Guadagni per dealer" icon={Store} testid="dealers-comparison-card" chartTestid="dealers-comparison-chart" height={Math.min(420, Math.max(250, dealersCmp.length * 46))}>
              <BarChart data={dealersCmp} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" horizontal={false} />
                <XAxis type="number" stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => euro(v, { maximumFractionDigits: 0 }).replace(" ", "\u00a0")} />
                <YAxis type="category" dataKey="dealer_name" stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} width={140} tick={({ x, y, payload }) => <foreignObject x={x - 140} y={y - 20} width={135} height={40}><div className="text-right text-xs leading-5 truncate"><EntityLink id={dealersCmp[payload.index]?.dealer_id} data-testid={`dashboard-dealer-link-${dealersCmp[payload.index]?.dealer_id}`}>{payload.value}</EntityLink></div></foreignObject>} />
                <Tooltip contentStyle={TIP} formatter={fmtBar} cursor={{ fill: "rgba(37,99,235,0.08)" }} />
                <Legend formatter={legend} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#2563EB" radius={[0, 6, 6, 0]} maxBarSize={18} />
                <Bar dataKey="commission" fill="#0D9488" radius={[0, 6, 6, 0]} maxBarSize={18} />
              </BarChart>
            </ChartCard>
          )}
        </div>
      )}

      {isAdmin && <AdminBriefing />}

      {stats?.role === "customer" && (
        <Card className="p-5">
          <p className="text-muted-foreground">Benvenuto nel tuo portale. Sfoglia il <button className="text-primary underline inline-flex items-center" data-testid="customer-overview-catalog" onClick={() => navigate("/app/catalogo")}>catalogo<ChevronRight className="w-3.5 h-3.5" /></button> per scoprire SIM, ricariche e smartphone disponibili.</p>
        </Card>
      )}
    </div>
  );
}
